import {
  LegalDocLayout,
  LP, LH3, LUl, LLi, LOl, LNote, LTable,
  type LegalSection,
} from '../legal/LegalDocLayout';

const sections: LegalSection[] = [
  {
    id: 'overview',
    heading: 'Overview',
    body: (
      <>
        <LP>
          The General Data Protection Regulation (EU) 2016/679 (<strong>"GDPR"</strong>) and the
          UK GDPR establish rights for individuals regarding their personal data and obligations
          for organisations that process it. Zykes Technologies LLP (<strong>"Zykes"</strong>)
          takes these obligations seriously and has designed GoGrabJob Business to support
          customers operating in the European Economic Area (EEA), the United Kingdom, and other
          jurisdictions with comparable data protection requirements.
        </LP>
        <LP>
          This page describes how Zykes is structured within the GDPR framework, the commitments
          we make to our customers, and the tools and documentation we provide to help customers
          meet their own compliance obligations.
        </LP>
        <LNote>
          This page is intended as an informational guide. It does not constitute legal advice.
          Customers should seek independent legal counsel to assess their own GDPR obligations and
          how GoGrabJob Business fits within their data processing activities.
        </LNote>
      </>
    ),
  },
  {
    id: 'roles',
    heading: 'Our Role Under GDPR',
    body: (
      <>
        <LP>
          GDPR distinguishes between <strong>data controllers</strong> (entities that determine
          the purposes and means of processing) and <strong>data processors</strong> (entities
          that process data on behalf of controllers).
        </LP>

        <LH3>Zykes as Data Processor</LH3>
        <LP>
          When you use GoGrabJob Business to manage recruiting and candidate data, <em>you</em>{' '}
          (the employer or recruitment agency) are the data controller. You determine which
          candidates to recruit, what data to collect, and how long to retain it. Zykes processes
          candidate personal data solely on your instructions, acting as a data processor under
          Article 28 GDPR.
        </LP>

        <LH3>Zykes as Data Controller</LH3>
        <LP>
          Zykes acts as a data controller for the personal data of:
        </LP>
        <LUl>
          <LLi>
            Customer account holders and Authorised Users (name, work email, billing information,
            usage data).
          </LLi>
          <LLi>
            Visitors to our marketing website (analytics, cookie data, contact form submissions).
          </LLi>
          <LLi>
            Our own staff, contractors, and job applicants to Zykes.
          </LLi>
        </LUl>
        <LP>
          Processing for which Zykes is the controller is governed by our{' '}
          <a
            href="/business/privacy-policy"
            className="text-violet-600 hover:underline dark:text-violet-400"
          >
            Privacy Policy
          </a>
          .
        </LP>
      </>
    ),
  },
  {
    id: 'legal-bases',
    heading: 'Legal Bases for Processing',
    body: (
      <>
        <LP>
          GDPR requires every act of processing personal data to have a lawful basis. When Zykes
          acts as a data processor on your behalf, your instructions determine the legal basis. As
          a data controller for our own processing, Zykes relies on the following bases:
        </LP>
        <LTable
          headers={['Processing Activity', 'Legal Basis', 'GDPR Article']}
          rows={[
            ['Account creation & subscription management', 'Performance of a contract', 'Art. 6(1)(b)'],
            ['Providing the GoGrabJob Business Services', 'Performance of a contract', 'Art. 6(1)(b)'],
            ['Billing & invoice management', 'Legal obligation (tax & accounting laws)', 'Art. 6(1)(c)'],
            ['Security monitoring & fraud prevention', 'Legitimate interests', 'Art. 6(1)(f)'],
            ['Product analytics & improvement', 'Legitimate interests', 'Art. 6(1)(f)'],
            ['Marketing to existing customers', 'Legitimate interests', 'Art. 6(1)(f)'],
            ['Marketing to prospects', 'Consent', 'Art. 6(1)(a)'],
            ['Analytics & marketing cookies', 'Consent', 'Art. 6(1)(a)'],
            ['Responding to data subject requests', 'Legal obligation', 'Art. 6(1)(c)'],
            ['Sharing with law enforcement (when lawfully required)', 'Legal obligation', 'Art. 6(1)(c)'],
          ]}
        />
        <LP>
          Where Zykes relies on legitimate interests, we conduct a Legitimate Interests Assessment
          (LIA) to balance our interests against the rights and freedoms of data subjects.
          Summaries of our LIAs are available on request.
        </LP>
      </>
    ),
  },
  {
    id: 'data-subject-rights',
    heading: 'Data Subject Rights',
    body: (
      <>
        <LP>
          GDPR grants individuals in the EEA/UK eight fundamental rights regarding their personal
          data. The following table summarises each right and how Zykes supports it:
        </LP>
        <LTable
          headers={['Right', 'What It Means', 'How to Exercise']}
          rows={[
            [
              'Right of Access (Art. 15)',
              'Request a copy of your personal data and information about how it is processed.',
              'Email privacy@gograbjob.com or use the DSR form in Account Settings.',
            ],
            [
              'Right to Rectification (Art. 16)',
              'Request correction of inaccurate or incomplete personal data.',
              'Correct directly in Account Settings or submit a DSR.',
            ],
            [
              'Right to Erasure (Art. 17)',
              'Request deletion of your personal data ("right to be forgotten"), where no legitimate purpose for retention exists.',
              'Submit a DSR — we respond within 30 days.',
            ],
            [
              'Right to Restriction (Art. 18)',
              'Request that we restrict processing of your data pending resolution of a dispute or accuracy check.',
              'Submit a DSR specifying the restriction basis.',
            ],
            [
              'Right to Portability (Art. 20)',
              'Receive your data in a structured, machine-readable format (JSON/CSV) for transfer to another controller.',
              'Use the Data Export feature in Account Settings.',
            ],
            [
              'Right to Object (Art. 21)',
              'Object to processing based on legitimate interests, including direct marketing.',
              'Unsubscribe from marketing emails or submit a DSR.',
            ],
            [
              'Rights re: Automated Decisions (Art. 22)',
              'Not be subject to solely automated decisions that significantly affect you, without human review.',
              'Contact privacy@gograbjob.com to request human review of any AI-based output.',
            ],
            [
              'Right to Withdraw Consent',
              'Withdraw any previously given consent at any time, without affecting prior processing.',
              'Cookie Settings panel for cookies; unsubscribe for marketing emails.',
            ],
          ]}
        />
        <LNote>
          If you are a <strong>job applicant</strong> whose data was processed through GoGrabJob
          Business by an employer, your primary contact for data subject requests is the employer
          (the data controller). Zykes will assist the employer in responding to valid requests
          within the DPA timeframes.
        </LNote>
      </>
    ),
  },
  {
    id: 'how-to-exercise',
    heading: 'How to Exercise Your Rights',
    body: (
      <>
        <LP>
          To submit a Data Subject Request (DSR) to Zykes as a data controller:
        </LP>
        <LOl>
          <LLi>
            <strong>In-platform DSR form:</strong> Log into your GoGrabJob Business account →
            Settings → Privacy → Data Subject Request. The form routes to our privacy team and
            automatically records the submission timestamp.
          </LLi>
          <LLi>
            <strong>Email:</strong> Send your request to{' '}
            <a
              href="mailto:privacy@gograbjob.com"
              className="text-violet-600 hover:underline dark:text-violet-400"
            >
              privacy@gograbjob.com
            </a>{' '}
            with the subject line "Data Subject Request — [right being exercised]". Include your
            full name, account email, and a description of your request.
          </LLi>
        </LOl>
        <LP>
          <strong>Response timeline:</strong> We acknowledge DSRs within 5 business days and
          fulfil them within 30 calendar days. Where a request is complex or there is a high
          volume, we may extend by a further 60 days with notice and justification.
        </LP>
        <LP>
          <strong>Identity verification:</strong> To protect your data, we may ask you to verify
          your identity before processing the request. We will not use verification data for any
          other purpose.
        </LP>
        <LP>
          <strong>No fees:</strong> DSR requests are provided free of charge. For manifestly
          unfounded or excessive requests (e.g., repetitive requests with no new basis), we may
          charge a reasonable administrative fee, which we will notify you of in advance.
        </LP>
      </>
    ),
  },
  {
    id: 'dpa',
    heading: 'Data Processing Agreement (DPA)',
    body: (
      <>
        <LP>
          Article 28 of the GDPR requires data controllers to enter into a written agreement with
          each data processor governing the terms of processing. Zykes provides a GDPR-compliant
          DPA to all paid customers.
        </LP>
        <LP>
          <strong>The DPA includes:</strong>
        </LP>
        <LUl>
          <LLi>
            The subject matter, duration, nature, and purpose of processing on your behalf.
          </LLi>
          <LLi>
            Categories of personal data processed and categories of data subjects.
          </LLi>
          <LLi>
            Zykes's obligations: technical and organisational security measures, confidentiality
            obligations on our staff, sub-processor management, assistance with DSRs and audits.
          </LLi>
          <LLi>
            Your obligations: acting as a data controller in compliance with GDPR, providing lawful
            instructions to Zykes.
          </LLi>
          <LLi>
            Standard Contractual Clauses (SCCs) for international transfers (Controller-to-Processor
            module).
          </LLi>
          <LLi>
            Sub-processor list with notification process for changes.
          </LLi>
          <LLi>
            Return and deletion of data upon termination.
          </LLi>
        </LUl>
        <LP>
          <strong>How to access the DPA:</strong>
        </LP>
        <LUl>
          <LLi>
            <strong>Self-serve (Growth & Enterprise):</strong> Download from Account Settings →
            Legal Documents → Data Processing Agreement.
          </LLi>
          <LLi>
            <strong>On request (Starter):</strong> Email{' '}
            <a
              href="mailto:legal@gograbjob.com"
              className="text-violet-600 hover:underline dark:text-violet-400"
            >
              legal@gograbjob.com
            </a>
            .
          </LLi>
          <LLi>
            <strong>Custom DPA (Enterprise):</strong> Available for customers requiring bespoke
            terms or specific jurisdiction addenda. Minimum Enterprise tier required.
          </LLi>
        </LUl>
      </>
    ),
  },
  {
    id: 'international-transfers',
    heading: 'International Data Transfers',
    body: (
      <>
        <LP>
          GoGrabJob Business infrastructure is hosted primarily in India, with optional EEA-region
          deployment for Enterprise customers. Where personal data originating in the EEA or UK is
          transferred outside those jurisdictions (including to India), we ensure appropriate
          safeguards are in place.
        </LP>

        <LH3>Transfer Mechanisms</LH3>
        <LUl>
          <LLi>
            <strong>Standard Contractual Clauses (SCCs):</strong> We use the European
            Commission's 2021 SCCs (Controller-to-Processor module) for all EEA-originating
            transfers to India and the United States. These are incorporated into our DPA.
          </LLi>
          <LLi>
            <strong>UK IDTA:</strong> For transfers from the UK, we use the ICO's International
            Data Transfer Agreement (IDTA) addendum to the EU SCCs.
          </LLi>
          <LLi>
            <strong>Transfer Impact Assessments (TIAs):</strong> We conduct TIAs for non-adequate
            third countries to document that the SCCs are effective in practice, taking into
            account the legal framework of the destination country.
          </LLi>
        </LUl>

        <LH3>EEA Data Residency (Enterprise)</LH3>
        <LP>
          Enterprise customers may elect EEA-resident data storage, where all candidate and
          recruiter data is stored and processed on EU-region cloud infrastructure. This
          eliminates the need to rely on SCCs for primary data storage. Contact your account
          manager or{' '}
          <a
            href="mailto:enterprise@gograbjob.com"
            className="text-violet-600 hover:underline dark:text-violet-400"
          >
            enterprise@gograbjob.com
          </a>{' '}
          for details.
        </LP>
      </>
    ),
  },
  {
    id: 'sub-processors',
    heading: 'Sub-Processors',
    body: (
      <>
        <LP>
          Zykes engages third-party sub-processors to provide specific components of the Services
          (e.g., cloud infrastructure, email delivery, payment processing). All sub-processors are
          contractually bound by GDPR-equivalent data protection obligations.
        </LP>

        <LH3>Current Sub-Processors (Summary)</LH3>
        <LTable
          headers={['Sub-processor', 'Purpose', 'Data Processed', 'Location']}
          rows={[
            ['Amazon Web Services (AWS)', 'Cloud infrastructure, storage, compute', 'All customer and candidate data', 'India (ap-south-1), optional EU'],
            ['Stripe', 'Payment processing & billing', 'Billing name, card metadata, invoice history', 'USA / EEA'],
            ['SendGrid (Twilio)', 'Transactional email delivery', 'Recipient email, notification content', 'USA / EEA'],
            ['PostHog (self-hosted on AWS)', 'Product analytics', 'Usage events, masked UI interactions', 'India'],
            ['Intercom', 'Customer support chat', 'Name, email, support conversation content', 'USA (SCCs in place)'],
            ['Cloudflare', 'CDN, DDoS protection, WAF', 'IP address, request headers (no body)', 'Global edge'],
            ['Sentry', 'Error monitoring & debugging', 'Stack traces, anonymised user identifiers', 'USA (SCCs in place)'],
          ]}
        />
        <LP>
          The complete sub-processor list (including version history) is maintained in the{' '}
          <a
            href="/business/trust-center"
            className="text-violet-600 hover:underline dark:text-violet-400"
          >
            Trust Center
          </a>
          .
        </LP>
        <LP>
          <strong>Sub-processor changes:</strong> We provide 30 days' advance notice of material
          sub-processor additions or replacements via email to the account's primary contact and
          via an in-platform notification. Customers who object may terminate within the notice
          period per the DPA terms.
        </LP>
      </>
    ),
  },
  {
    id: 'breach-notification',
    heading: 'Data Breach Notification',
    body: (
      <>
        <LP>
          Zykes has a documented Incident Response Plan covering detection, containment, assessment,
          and notification of personal data breaches.
        </LP>
        <LP>
          Under GDPR Article 33, Zykes will:
        </LP>
        <LUl>
          <LLi>
            Notify affected customers without undue delay, and <strong>within 72 hours</strong> of
            becoming aware of a personal data breach that is likely to result in a risk to the
            rights and freedoms of data subjects.
          </LLi>
          <LLi>
            Provide in the notification: the nature of the breach, categories and approximate
            number of data subjects affected, likely consequences, and measures taken or proposed.
          </LLi>
          <LLi>
            Assist customers with their own notification obligations to supervisory authorities
            and, where required (Art. 34 GDPR), to affected data subjects.
          </LLi>
        </LUl>
        <LP>
          Customers are responsible for notifying their relevant supervisory authority within 72
          hours of receiving Zykes's breach notification, where required.
        </LP>
        <LP>
          To report a security vulnerability or suspected breach, contact{' '}
          <a
            href="mailto:security@gograbjob.com"
            className="text-violet-600 hover:underline dark:text-violet-400"
          >
            security@gograbjob.com
          </a>{' '}
          immediately.
        </LP>
      </>
    ),
  },
  {
    id: 'privacy-by-design',
    heading: 'Privacy by Design & Default',
    body: (
      <>
        <LP>
          Article 25 GDPR requires data processors and controllers to implement data protection
          "by design and by default". At Zykes, this means:
        </LP>
        <LUl>
          <LLi>
            <strong>Data minimisation:</strong> We only collect personal data that is adequate,
            relevant, and limited to what is necessary for the stated purpose.
          </LLi>
          <LLi>
            <strong>Purpose limitation:</strong> Data collected for one purpose (e.g., hiring
            pipeline management) is not repurposed without a compatible basis.
          </LLi>
          <LLi>
            <strong>Storage limitation:</strong> Configurable data retention periods; automated
            archiving and deletion workflows available to customers.
          </LLi>
          <LLi>
            <strong>Pseudonymisation:</strong> Candidate identifiers are pseudonymised in analytics
            and logging pipelines.
          </LLi>
          <LLi>
            <strong>Privacy defaults:</strong> New accounts default to the most privacy-friendly
            settings (e.g., analytics cookies are off until explicitly enabled; candidate data
            visibility is role-restricted by default).
          </LLi>
          <LLi>
            <strong>Privacy impact assessments (DPIAs):</strong> We conduct DPIAs for new
            features involving large-scale processing of special category data or systematic
            monitoring. Results are available to Enterprise customers under NDA.
          </LLi>
        </LUl>
      </>
    ),
  },
  {
    id: 'dpo',
    heading: 'Data Protection Officer',
    body: (
      <>
        <LP>
          Zykes has appointed a Data Protection Officer (DPO) to oversee compliance with the GDPR
          and to act as a point of contact for data subjects and supervisory authorities.
        </LP>
        <LP>
          <strong>DPO Contact:</strong>
          <br />
          Email:{' '}
          <a
            href="mailto:dpo@gograbjob.com"
            className="text-violet-600 hover:underline dark:text-violet-400"
          >
            dpo@gograbjob.com
          </a>
          <br />
          Address: Zykes Technologies LLP, India
        </LP>
        <LP>
          The DPO is independent and reports directly to the leadership team. You may contact the
          DPO directly for any GDPR-related matters, including exercising your rights or escalating
          a privacy concern that you feel has not been adequately addressed.
        </LP>
      </>
    ),
  },
  {
    id: 'supervisory-authority',
    heading: 'Supervisory Authority',
    body: (
      <>
        <LP>
          If you are located in the EEA and believe that Zykes has not addressed your data
          protection concern adequately, you have the right to lodge a complaint with the
          competent national supervisory authority in your EU member state.
        </LP>
        <LP>
          Examples of EEA supervisory authorities:
        </LP>
        <LUl>
          <LLi>
            <strong>Ireland:</strong> Data Protection Commission (DPC) —{' '}
            <a
              href="https://www.dataprotection.ie"
              target="_blank"
              rel="noopener noreferrer"
              className="text-violet-600 hover:underline dark:text-violet-400"
            >
              dataprotection.ie
            </a>
          </LLi>
          <LLi>
            <strong>Germany:</strong> Bundesbeauftragte für den Datenschutz (BfDI) and relevant
            Länder DPAs
          </LLi>
          <LLi>
            <strong>France:</strong> Commission Nationale de l'Informatique et des Libertés (CNIL)
          </LLi>
          <LLi>
            <strong>UK:</strong> Information Commissioner's Office (ICO) —{' '}
            <a
              href="https://ico.org.uk"
              target="_blank"
              rel="noopener noreferrer"
              className="text-violet-600 hover:underline dark:text-violet-400"
            >
              ico.org.uk
            </a>
          </LLi>
        </LUl>
        <LP>
          For India residents, once the Data Protection Board of India is established under the
          DPDPA 2023, it will serve as the relevant supervisory authority. Until then, grievances
          may be directed to our Grievance Officer at{' '}
          <a
            href="mailto:grievance@gograbjob.com"
            className="text-violet-600 hover:underline dark:text-violet-400"
          >
            grievance@gograbjob.com
          </a>
          .
        </LP>
      </>
    ),
  },
];

export function GdprCompliancePage() {
  return (
    <LegalDocLayout
      category="Legal · Compliance"
      title="GDPR Compliance"
      subtitle="How GoGrabJob Business supports your European data protection obligations — our role, your rights, and the tools we provide."
      effectiveDate="1 April 2026"
      lastUpdated="14 April 2026"
      version="2.0"
      sections={sections}
      contactEmail="dpo@gograbjob.com"
    />
  );
}
